import { joinPages, sizesOf, splitPages, type PageSize } from '../editor/pageFormat';

/*
 * เลือกหน้าที่จะส่งออก (choices 1ก–5ก), free of DOM/Fabric for the tests (tests/export-pages.test.ts):
 *   ทั้งหมด / หน้านี้ / ตั้งค่าเอง — "ตั้งค่าเอง" is a typed range ("1-3, 5") kept in step with ticked previews
 *   every file type: PNG/JPG one file a page; the PDFs only the chosen pages, in page order (or one PDF a page)
 *   several files: one by one, or one ZIP (a switch); the choice of pages is not remembered
 * Page numbers here are 0-based; what people see and type is 1-based.
 */

export type PagesMode = 'all' | 'this' | 'pick';

/** "1-3, 5, 8-10" → [0,1,2,4,7,8,9]; a reason instead when it cannot be used (never silently fixed) */
export function parseRange(text: string, count: number): { ok: true; pages: number[] } | { ok: false; error: string } {
  const t = text.replace(/[–—−]/g, '-').trim();
  if (!t) return { ok: false, error: 'ยังไม่ได้เลือกหน้า พิมพ์เลขหน้า เช่น 1-3, 5 หรือแตะรูปหน้าที่ต้องการ' };
  const set = new Set<number>();
  for (const raw of t.split(/[,\s]+/).filter(Boolean)) {
    const m = /^(\d+)(?:-(\d+))?$/.exec(raw);
    if (!m) return { ok: false, error: `อ่าน "${raw}" ไม่ได้ ใช้ตัวเลขกับขีด เช่น 1-3, 5` };
    const a = Number(m[1]), b = m[2] === undefined ? a : Number(m[2]);
    if (a < 1 || b < 1) return { ok: false, error: 'เลขหน้าเริ่มที่ 1' };
    if (a > count || b > count) return { ok: false, error: `งานนี้มี ${count} หน้า (พิมพ์ "${raw}" เกินจำนวนหน้า)` };
    if (b < a) return { ok: false, error: `"${raw}" กลับด้าน ให้ใส่เลขน้อยก่อน เช่น ${b}-${a}` };
    for (let k = a; k <= b; k++) set.add(k - 1);
  }
  return { ok: true, pages: [...set].sort((x, y) => x - y) };
}

/** [0,1,2,4] → "1-3, 5" (what the typed box shows after ticking previews) */
export function formatRange(pages: number[]): string {
  const s = [...new Set(pages)].sort((a, b) => a - b);
  const out: string[] = [];
  for (let i = 0; i < s.length; i++) {
    let j = i;
    while (j + 1 < s.length && s[j + 1] === s[j] + 1) j++;
    out.push(j > i ? `${s[i] + 1}-${s[j] + 1}` : String(s[i] + 1));
    i = j;
  }
  return out.join(', ');
}

/**
 * The design with only `pages` (in page order), each keeping its own size, bleed and name: what every PDF is made
 * from, so it holds exactly the chosen pages. `fallback` is page 1's size when the JSON does not say it.
 */
export function pickPages(json: string, pages: number[], fallback: PageSize): string {
  if (!pages.length) throw new Error('ยังไม่ได้เลือกหน้าที่จะส่งออก');
  const s = splitPages(json);
  for (const k of pages) if (s.pages[k] === undefined) throw new Error(`ไม่มีหน้า ${k + 1} ในงานนี้ (งานเปลี่ยนไป) เลือกหน้าใหม่อีกครั้ง`);
  const sizes = sizesOf(s, fallback), first = sizes[pages[0]];
  const meta: Record<string, unknown> = { ...s.meta, size: { w: Math.round(first.w), h: Math.round(first.h) } };
  if (first.bleedMm !== undefined) meta.print = { ...(s.meta.print as object | undefined), bleedMm: first.bleedMm };
  else delete meta.print;
  return joinPages(meta, pages.map(k => s.pages[k]), pages.map(k => sizes[k]), s.names ? pages.map(k => s.names![k]) : null);
}

/** file names inside a ZIP: a repeat gets " (2)", " (3)" before its extension (a ZIP cannot hold two of one name) */
export function uniqueNames(names: string[]): string[] {
  const used = new Set<string>();
  return names.map(n => {
    let name = n, k = 2;
    const dot = n.lastIndexOf('.'), stem = dot > 0 ? n.slice(0, dot) : n, ext = dot > 0 ? n.slice(dot) : '';
    while (used.has(name.toLowerCase())) name = `${stem} (${k++})${ext}`;
    used.add(name.toLowerCase());
    return name;
  });
}
